/**
 * Onglet « Profil » de la page Compte — nom, fonction, langue et avatar modifiables. E-mail et
 * fournisseur sont en lecture seule : l'e-mail est la clé d'identité du compte (cf.
 * `docs/architecture/authentification.md`), le fournisseur en découle directement. Affichés en
 * paire libellé/valeur statique plutôt qu'en `<input disabled>` — un champ qui ressemble à un
 * champ éditable pour une valeur qui ne le sera jamais est trompeur (§ audit exiUI 2026-09-27).
 *
 * Planche B (207:3129, `@exidis/ui` 1.5.0) : `CELLS_PLANCHE`/`CELL_PLANCHE`, cellules fermées
 * séparées par la gouttière de 16 px du paquet. La
 * cellule Identité occupe les deux rangées à gauche ; Organisations et Équipes s'empilent à
 * droite — l'auto-placement CSS Grid (rangée par rangée, en sautant les cellules déjà prises)
 * suffit, sans `grid-template-areas` explicite.
 *
 * Pas de `PageHeader` ici : il est porté par `AccountPage`, commun aux quatre onglets.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { messageModeleOrganisation } from '../lib/org-errors.js';
import { useAsync } from '../hooks/useAsync.js';
import { useSession } from '../contexts/SessionContext.jsx';
import { appAlert, appPrompt } from '../dialogs/service.js';
import { notifyOrganizationsChanged } from '../lib/organizationsBus.js';
import { Empty, Eyebrow, SaveStatus } from '../components/common.jsx';
import { nomEspace } from '../lib/nom-espace.js';
import { RoleBadge } from '../components/RoleBadge.jsx';
import {
  BTN_GHOST,
  BTN_GHOST_SM,
  CELLS_PLANCHE,
  CELL_PLANCHE,
  FORM_FIELD,
  FORM_LABEL,
  INPUT,
  SELECT,
  TD,
  TH,
} from '../ui/theme.js';

/** Littéraux dans l'objet, pas de clé calculée — même convention que `TeamsPage::ROLE_KEYS`. */
const PROVIDER_LABEL_KEYS = {
  microsoft: 'profile.providers.microsoft',
  github: 'profile.providers.github',
  google: 'profile.providers.google',
  local: 'profile.providers.local',
};

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { refresh: refreshSession } = useSession();
  const {
    data: profile,
    error,
    loading,
    reload,
  } = useAsync((signal) => api.get('/me', { signal }), []);

  const [form, setForm] = useState({ name: '', jobTitle: '', avatarUrl: '', locale: 'fr' });
  const [saveState, setSaveState] = useState('idle');
  const [savedAt, setSavedAt] = useState(null);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: suggestionsData, reload: reloadSuggestions } = useAsync(
    (signal) => api.get('/me/organization-suggestions', { signal }),
    [],
  );
  const suggestions = suggestionsData?.data ?? [];

  // La saisie part vide et se remplit dès que le profil arrive — pas de valeurs par défaut
  // devinées, sinon un enregistrement précoce écraserait un champ encore non chargé.
  useEffect(() => {
    if (!profile?.data?.user) return;
    const { name, jobTitle, avatarUrl, locale } = profile.data.user;
    setForm({
      name: name ?? '',
      jobTitle: jobTitle ?? '',
      avatarUrl: avatarUrl ?? '',
      locale: locale ?? 'fr',
    });
  }, [profile]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setSaveState('loading');
    try {
      await api.patch('/me', {
        name: form.name.trim(),
        jobTitle: form.jobTitle.trim() || null,
        avatarUrl: form.avatarUrl.trim() || null,
        locale: form.locale,
      });
      await reload();
      // Le nom affiché dans `AppShell` vient de la session, pas de cette page : sans ce
      // rafraîchissement, un changement de nom n'apparaîtrait qu'au prochain rechargement.
      await refreshSession();
      setSaveState('saved');
      setSavedAt(new Date());
    } catch (submitError) {
      setSaveState('error');
      await appAlert(t('profile.saveFailed', { message: submitError.message }), {
        title: t('profile.saveFailedTitle'),
      });
    }
  };

  const createOrganization = async () => {
    const name = await appPrompt(t('profile.createOrgPromptMessage'), '', {
      title: t('profile.createOrgPromptTitle'),
    });
    if (!name || !name.trim()) return;
    setCreatingOrg(true);
    try {
      await api.post('/organizations', { name: name.trim() });
      await reload();
      notifyOrganizationsChanged();
    } catch (submitError) {
      await appAlert(t('profile.createOrgFailed', { message: submitError.message }), {
        title: t('profile.createOrgPromptTitle'),
      });
    } finally {
      setCreatingOrg(false);
    }
  };

  const requestToJoin = async (suggestion) => {
    setBusy(true);
    try {
      await api.post(`/organizations/${suggestion.id}/join-requests`);
      await reloadSuggestions();
      await reload();
    } catch (submitError) {
      await appAlert(
        t('profile.joinRequestFailed', {
          message: messageModeleOrganisation(submitError, t) ?? submitError.message,
        }),
        {
          title: t('profile.joinRequestFailedTitle'),
        },
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="p-4 text-sm text-ink-soft">{t('profile.loading')}</p>;
  }

  if (error) {
    return (
      <div className="p-4">
        <Empty>{t('profile.loadError', { message: error.message })}</Empty>
      </div>
    );
  }

  const { user, organizations = [], teams = [] } = profile.data;
  // ORG-4 : au plus une organisation par compte (409 ALREADY_IN_ORGANIZATION côté serveur).
  // `/me` reprend `listMyOrganizations`, non filtrée par statut : une adhésion en demande ou
  // suspendue ferme aussi la création — même règle que `organizationEntry` dans `AppShell`.
  const canCreateOrganization = organizations.length === 0;
  const providerLabel = PROVIDER_LABEL_KEYS[user.provider]
    ? t(PROVIDER_LABEL_KEYS[user.provider])
    : user.provider;
  const savedAtLabel = savedAt
    ? new Intl.DateTimeFormat(i18n.resolvedLanguage, { hour: '2-digit', minute: '2-digit' }).format(
        savedAt,
      )
    : null;

  return (
    <div className={`${CELLS_PLANCHE} grid-cols-1 md:grid-cols-2`}>
      <div className={`${CELL_PLANCHE} bg-paper p-6 md:row-span-2`}>
        <span className="ticks" aria-hidden="true" />
        <Eyebrow>{t('profile.identityTitle')}</Eyebrow>

        <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-ink-soft">{t('profile.email')}</dt>
          <dd className="text-ink">{user.email}</dd>
          <dt className="text-ink-soft">{t('profile.provider')}</dt>
          <dd className="text-ink">{providerLabel}</dd>
        </dl>
        <p className="mt-1 text-xs text-ink-faint">{t('profile.emailReadOnlyReason')}</p>

        <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-3">
          <label className={FORM_FIELD}>
            <span className={FORM_LABEL}>{t('profile.name')}</span>
            <input
              className={INPUT}
              value={form.name}
              onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
              required
            />
          </label>

          <label className={FORM_FIELD}>
            <span className={FORM_LABEL}>{t('profile.jobTitle')}</span>
            <input
              className={INPUT}
              value={form.jobTitle}
              onChange={(event) => setForm((f) => ({ ...f, jobTitle: event.target.value }))}
            />
          </label>

          <label className={FORM_FIELD}>
            <span className={FORM_LABEL}>{t('profile.avatarUrl')}</span>
            <input
              className={INPUT}
              value={form.avatarUrl}
              onChange={(event) => setForm((f) => ({ ...f, avatarUrl: event.target.value }))}
            />
          </label>

          <label className={FORM_FIELD}>
            <span className={FORM_LABEL}>{t('profile.locale')}</span>
            <select
              className={SELECT}
              value={form.locale}
              onChange={(event) => setForm((f) => ({ ...f, locale: event.target.value }))}
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </label>

          <div className="flex items-center gap-3">
            <button type="submit" className={BTN_GHOST} disabled={saveState === 'loading'}>
              {t('profile.save')}
            </button>
            {saveState !== 'idle' && (
              <SaveStatus
                state={saveState === 'error' ? 'error' : saveState}
                busy={saveState === 'loading'}
                detail={saveState === 'saved' ? savedAtLabel : undefined}
              />
            )}
          </div>
        </form>
      </div>

      <div className={`${CELL_PLANCHE} bg-paper p-6`}>
        <span className="ticks" aria-hidden="true" />
        <div className="flex items-center justify-between gap-2">
          <Eyebrow>{t('profile.organizations')}</Eyebrow>
          {canCreateOrganization && (
            <button
              type="button"
              className={BTN_GHOST_SM}
              onClick={createOrganization}
              disabled={creatingOrg}
            >
              {t('profile.createOrg')}
            </button>
          )}
        </div>

        {organizations.length === 0 ? (
          <div className="mt-2">
            <Empty>{t('profile.noOrganizations')}</Empty>
          </div>
        ) : (
          <table className="mt-3 w-full border-collapse">
            <thead>
              <tr>
                <th className={TH}>{t('profile.columnName')}</th>
                <th className={TH}>{t('profile.columnRole')}</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((organization) => (
                <tr key={organization.id}>
                  <td className={TD}>
                    <Link
                      to={`/organisation/${organization.id}`}
                      className="text-ink hover:text-accent-ink"
                    >
                      {nomEspace(organization, t)}
                    </Link>
                  </td>
                  <td className={TD}>
                    <RoleBadge role={organization.role} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {suggestions.length > 0 && (
          <div className="mt-4 border-t-2 border-line pt-3">
            <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
              {t('profile.orgSuggestionsTitle')}
            </p>
            <ul className="mt-2 flex flex-col gap-2">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.id}
                  className="flex items-center justify-between gap-2 border-b-2 border-line-soft py-2"
                >
                  <span className="text-sm text-ink">{suggestion.name}</span>
                  <button
                    type="button"
                    className={BTN_GHOST_SM}
                    disabled={busy}
                    onClick={() => requestToJoin(suggestion)}
                  >
                    {t('profile.requestToJoin')}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className={`${CELL_PLANCHE} bg-paper p-6`}>
        <span className="ticks" aria-hidden="true" />
        <div className="flex items-center justify-between gap-2">
          <Eyebrow>{t('profile.teams')}</Eyebrow>
          <Link to="/equipes" className={BTN_GHOST_SM}>
            {t('profile.manageTeams')}
          </Link>
        </div>
        {teams.length === 0 ? (
          <div className="mt-2">
            <Empty>{t('profile.noTeams')}</Empty>
          </div>
        ) : (
          <table className="mt-3 w-full border-collapse">
            <thead>
              <tr>
                <th className={TH}>{t('profile.columnName')}</th>
                <th className={TH}>{t('profile.columnRole')}</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.id}>
                  <td className={TD}>
                    <span className="text-ink">{team.name}</span>
                  </td>
                  <td className={TD}>
                    <RoleBadge role={team.role} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
